// Geografia mapy dostaw — Europa, prawdziwe dane kartograficzne.
// Kontury krajów: Natural Earth (world-atlas) 1:50m pobierane online,
// dekodowane do surowych ringów lon/lat i cache'owane w localStorage.
// Fallback offline: kontur Polski odprojektowany z wbudowanej ścieżki 1:50m.
//
// Projekcja jest dynamiczna: kadr dopasowuje się do huba i punktów dostaw.
// Gdy wszystko mieści się w Polsce, widok = Polska (jak dotychczas); wysyłki
// zagraniczne (np. Niemcy) automatycznie poszerzają kadr na Europę.
import { useEffect, useState } from 'react';

export const MAP_W = 1000;
export const MAP_H = 720;
const PAD = 16;

export type LatLon = { lat: number; lon: number };
export type GeoPlace = LatLon & { label: string };

/* ------------------------------------------------------------------ */
/* Miasta: [lat, lon] — rzeczywiste współrzędne geograficzne.          */
/* ------------------------------------------------------------------ */
const CITIES_GEO: Record<string, [number, number]> = {
  // Polska
  Warszawa: [52.2297, 21.0122],
  'Kraków': [50.0647, 19.945],
  'Wrocław': [51.1079, 17.0385],
  'Poznań': [52.4064, 16.9252],
  'Gdańsk': [54.352, 18.6466],
  'Łódź': [51.7592, 19.456],
  Szczecin: [53.4285, 14.5528],
  Lublin: [51.2465, 22.5684],
  Katowice: [50.2649, 19.0238],
  'Rzeszów': [50.0412, 21.9991],
  Bydgoszcz: [53.1235, 18.0084],
  'Białystok': [53.1325, 23.1688],
  Wieliczka: [49.9823, 20.0658],
  'Niepołomice': [50.0344, 20.22],
  Kielce: [50.8661, 20.6286],
  Olsztyn: [53.7784, 20.4801],
  'Toruń': [53.0138, 18.5984],
  Gdynia: [54.5189, 18.5305],
  'Częstochowa': [50.8118, 19.1203],
  Radom: [51.4027, 21.1471],
  'Tarnów': [50.0121, 20.9858],
  'Nowy Sącz': [49.6249, 20.6915],
  Zakopane: [49.2992, 19.9496],
  Gliwice: [50.2945, 18.6714],
  Opole: [50.6751, 17.9213],
  'Zielona Góra': [51.9356, 15.5062],
  'Gorzów Wielkopolski': [52.7368, 15.2288],
  'Bielsko-Biała': [49.8224, 19.0584],
  'Płock': [52.5463, 19.7065],
  Koszalin: [54.1944, 16.1722],
  'Słupsk': [54.4641, 17.0287],
  'Elbląg': [54.1522, 19.4088],
  Kalisz: [51.7611, 18.0911],
  Legnica: [51.2101, 16.1619],
  // Niemcy
  Berlin: [52.52, 13.405],
  Hamburg: [53.5511, 9.9937],
  'München': [48.1351, 11.582],
  'Köln': [50.9375, 6.9603],
  'Frankfurt am Main': [50.1109, 8.6821],
  Stuttgart: [48.7758, 9.1829],
  'Düsseldorf': [51.2277, 6.7735],
  Dortmund: [51.5136, 7.4653],
  Leipzig: [51.3397, 12.3731],
  Dresden: [51.0504, 13.7373],
  Hannover: [52.3759, 9.732],
  'Nürnberg': [49.4521, 11.0767],
  Bremen: [53.0793, 8.8017],
  // Czechy / Słowacja / Austria / Węgry
  Praha: [50.0755, 14.4378],
  Brno: [49.1951, 16.6068],
  Ostrava: [49.8209, 18.2625],
  'Plzeň': [49.7384, 13.3736],
  Bratislava: [48.1486, 17.1077],
  'Košice': [48.7164, 21.2611],
  Wien: [48.2082, 16.3738],
  Graz: [47.0707, 15.4395],
  Linz: [48.3069, 14.2858],
  Salzburg: [47.8095, 13.055],
  Budapest: [47.4979, 19.0402],
  // Francja / Beneluks
  Paris: [48.8566, 2.3522],
  Lyon: [45.764, 4.8357],
  Marseille: [43.2965, 5.3698],
  Lille: [50.6292, 3.0573],
  Toulouse: [43.6047, 1.4442],
  Bordeaux: [44.8378, -0.5792],
  Strasbourg: [48.5734, 7.7521],
  Nantes: [47.2184, -1.5536],
  Amsterdam: [52.3676, 4.9041],
  Rotterdam: [51.9244, 4.4777],
  'Den Haag': [52.0705, 4.3007],
  Utrecht: [52.0907, 5.1214],
  Eindhoven: [51.4416, 5.4697],
  Brussels: [50.8503, 4.3517],
  Antwerpen: [51.2194, 4.4025],
  Gent: [51.0543, 3.7174],
  Luxembourg: [49.6116, 6.1319],
  // Wyspy Brytyjskie
  London: [51.5074, -0.1278],
  Manchester: [53.4808, -2.2426],
  Birmingham: [52.4862, -1.8904],
  Leeds: [53.8008, -1.5491],
  Glasgow: [55.8642, -4.2518],
  Edinburgh: [55.9533, -3.1883],
  Dublin: [53.3498, -6.2603],
  // Iberia / Włochy / Szwajcaria
  Madrid: [40.4168, -3.7038],
  Barcelona: [41.3851, 2.1734],
  Valencia: [39.4699, -0.3763],
  Sevilla: [37.3891, -5.9845],
  Bilbao: [43.263, -2.935],
  Lisboa: [38.7223, -9.1393],
  Porto: [41.1579, -8.6291],
  Roma: [41.9028, 12.4964],
  Milano: [45.4642, 9.19],
  Napoli: [40.8518, 14.2681],
  Torino: [45.0703, 7.6869],
  Bologna: [44.4949, 11.3426],
  Firenze: [43.7696, 11.2558],
  Venezia: [45.4408, 12.3155],
  'Zürich': [47.3769, 8.5417],
  'Genève': [46.2044, 6.1432],
  Basel: [47.5596, 7.5886],
  Bern: [46.948, 7.4474],
  // Skandynawia / Bałtyk
  'København': [55.6761, 12.5683],
  Aarhus: [56.1629, 10.2039],
  Stockholm: [59.3293, 18.0686],
  'Göteborg': [57.7089, 11.9746],
  'Malmö': [55.605, 13.0038],
  Oslo: [59.9139, 10.7522],
  Bergen: [60.3913, 5.3221],
  Helsinki: [60.1699, 24.9384],
  Tampere: [61.4978, 23.761],
  Tallinn: [59.437, 24.7536],
  Riga: [56.9496, 24.1052],
  Vilnius: [54.6872, 25.2797],
  Kaunas: [54.8985, 23.9036],
  'Klaipėda': [55.7033, 21.1443],
  // Europa Wschodnia / Bałkany
  'Kyiv': [50.4501, 30.5234],
  Lviv: [49.8397, 24.0297],
  Odesa: [46.4825, 30.7233],
  Minsk: [53.9006, 27.559],
  'București': [44.4268, 26.1025],
  'Cluj-Napoca': [46.7712, 23.6236],
  'Timișoara': [45.7489, 21.2087],
  Sofia: [42.6977, 23.3219],
  Plovdiv: [42.1354, 24.7453],
  Athens: [37.9838, 23.7275],
  Thessaloniki: [40.6401, 22.9444],
  Zagreb: [45.815, 15.9819],
  Split: [43.5081, 16.4402],
  Ljubljana: [46.0569, 14.5058],
  Beograd: [44.7866, 20.4489],
  Sarajevo: [43.8563, 18.4131],
  Skopje: [41.9973, 21.428],
  Tirana: [41.3275, 19.8187],
  'Chișinău': [47.0105, 28.8638],
};

// Egzonimy (PL/EN) -> klucz słownika. Porównanie po normalizacji.
const CITY_ALIASES: Record<string, string> = {
  monachium: 'München', munich: 'München',
  kolonia: 'Köln', cologne: 'Köln',
  frankfurt: 'Frankfurt am Main',
  praga: 'Praha', prague: 'Praha',
  wieden: 'Wien', vienna: 'Wien',
  budapeszt: 'Budapest',
  paryz: 'Paris',
  haga: 'Den Haag', 'the hague': 'Den Haag',
  bruksela: 'Brussels', bruxelles: 'Brussels', brussel: 'Brussels',
  antwerpia: 'Antwerpen', antwerp: 'Antwerpen',
  luksemburg: 'Luxembourg',
  londyn: 'London',
  edynburg: 'Edinburgh',
  madryt: 'Madrid',
  walencja: 'Valencia',
  lizbona: 'Lisboa', lisbon: 'Lisboa',
  rzym: 'Roma', rome: 'Roma',
  mediolan: 'Milano', milan: 'Milano',
  neapol: 'Napoli', naples: 'Napoli',
  turyn: 'Torino', turin: 'Torino',
  bolonia: 'Bologna',
  florencja: 'Firenze', florence: 'Firenze',
  wenecja: 'Venezia', venice: 'Venezia',
  zurych: 'Zürich', zurich: 'Zürich',
  genewa: 'Genève', geneva: 'Genève',
  bazylea: 'Basel',
  kopenhaga: 'København', copenhagen: 'København',
  sztokholm: 'Stockholm',
  ryga: 'Riga',
  wilno: 'Vilnius',
  kowno: 'Kaunas',
  klajpeda: 'Klaipėda',
  kijow: 'Kyiv', kiev: 'Kyiv',
  lwow: 'Lviv',
  minsk: 'Minsk',
  bukareszt: 'București', bucharest: 'București',
  ateny: 'Athens', athina: 'Athens',
  saloniki: 'Thessaloniki',
  zagrzeb: 'Zagreb',
  lublana: 'Ljubljana',
  belgrad: 'Beograd', belgrade: 'Beograd',
  sarajewo: 'Sarajevo',
  kiszyniow: 'Chișinău', chisinau: 'Chișinău',
};

/* ------------------------------------------------------------------ */
/* Regiony zagraniczne: miasto -> jednostka administracyjna I rzędu.   */
/* Nazwy lokalne (jak w polu `province` Shopify), aby zlewały się z     */
/* danymi rzeczywistymi. Kraj ma własny ranking — to NIE jest region.  */
/* ------------------------------------------------------------------ */
const CITY_REGIONS: Record<string, string> = {
  // Niemcy (Bundesländer)
  Berlin: 'Berlin', Hamburg: 'Hamburg', 'München': 'Bayern', 'Nürnberg': 'Bayern',
  'Köln': 'Nordrhein-Westfalen', 'Düsseldorf': 'Nordrhein-Westfalen', Dortmund: 'Nordrhein-Westfalen',
  'Frankfurt am Main': 'Hessen', Stuttgart: 'Baden-Württemberg', Leipzig: 'Sachsen', Dresden: 'Sachsen',
  Hannover: 'Niedersachsen', Bremen: 'Bremen',
  // Czechy / Słowacja / Austria / Węgry
  Praha: 'Praha', Brno: 'Jihomoravský kraj', Ostrava: 'Moravskoslezský kraj', 'Plzeň': 'Plzeňský kraj',
  Bratislava: 'Bratislavský kraj', 'Košice': 'Košický kraj',
  Wien: 'Wien', Graz: 'Steiermark', Linz: 'Oberösterreich', Salzburg: 'Salzburg',
  Budapest: 'Budapest',
  // Francja / Beneluks
  Paris: 'Île-de-France', Lyon: 'Auvergne-Rhône-Alpes', Marseille: "Provence-Alpes-Côte d'Azur",
  Lille: 'Hauts-de-France', Toulouse: 'Occitanie', Bordeaux: 'Nouvelle-Aquitaine',
  Strasbourg: 'Grand Est', Nantes: 'Pays de la Loire',
  Amsterdam: 'Noord-Holland', Rotterdam: 'Zuid-Holland', 'Den Haag': 'Zuid-Holland',
  Utrecht: 'Utrecht', Eindhoven: 'Noord-Brabant',
  Brussels: 'Brussel', Antwerpen: 'Antwerpen', Gent: 'Oost-Vlaanderen', Luxembourg: 'Luxembourg',
  // Wyspy Brytyjskie
  London: 'England', Manchester: 'England', Birmingham: 'England', Leeds: 'England',
  Glasgow: 'Scotland', Edinburgh: 'Scotland', Dublin: 'Leinster',
  // Iberia / Włochy / Szwajcaria
  Madrid: 'Comunidad de Madrid', Barcelona: 'Cataluña', Valencia: 'Comunidad Valenciana',
  Sevilla: 'Andalucía', Bilbao: 'País Vasco', Lisboa: 'Lisboa', Porto: 'Porto',
  Roma: 'Lazio', Milano: 'Lombardia', Napoli: 'Campania', Torino: 'Piemonte',
  Bologna: 'Emilia-Romagna', Firenze: 'Toscana', Venezia: 'Veneto',
  'Zürich': 'Zürich', 'Genève': 'Genève', Basel: 'Basel-Stadt', Bern: 'Bern',
  // Skandynawia / Bałtyk
  'København': 'Hovedstaden', Aarhus: 'Midtjylland',
  Stockholm: 'Stockholm', 'Göteborg': 'Västra Götaland', 'Malmö': 'Skåne',
  Oslo: 'Oslo', Bergen: 'Vestland', Helsinki: 'Uusimaa', Tampere: 'Pirkanmaa',
  Tallinn: 'Harju', Riga: 'Rīga', Vilnius: 'Vilniaus apskritis', Kaunas: 'Kauno apskritis',
  'Klaipėda': 'Klaipėdos apskritis',
  // Europa Wschodnia / Bałkany
  'Kyiv': 'Kyiv', Lviv: 'Lvivska oblast', Odesa: 'Odeska oblast', Minsk: 'Minsk',
  'București': 'București', 'Cluj-Napoca': 'Cluj', 'Timișoara': 'Timiș',
  Sofia: 'Sofia', Plovdiv: 'Plovdiv', Athens: 'Attica', Thessaloniki: 'Central Macedonia',
  Zagreb: 'Zagreb', Split: 'Split-Dalmatia', Ljubljana: 'Osrednjeslovenska',
  Beograd: 'Beograd', Sarajevo: 'Sarajevo', Skopje: 'Skopje', Tirana: 'Tiranë',
  'Chișinău': 'Chișinău',
};

/* ------------------------------------------------------------------ */
/* Kraje: polska nazwa, centroid (fallback dla nieznanych miast)       */
/* i id Natural Earth (ISO 3166-1 numeric) do konturu.                 */
/* ------------------------------------------------------------------ */
export const COUNTRIES: Record<string, { name: string; lat: number; lon: number; topoId: string }> = {
  PL: { name: 'Polska', lat: 52.1, lon: 19.4, topoId: '616' },
  DE: { name: 'Niemcy', lat: 51.1, lon: 10.4, topoId: '276' },
  CZ: { name: 'Czechy', lat: 49.8, lon: 15.5, topoId: '203' },
  SK: { name: 'Słowacja', lat: 48.7, lon: 19.7, topoId: '703' },
  AT: { name: 'Austria', lat: 47.6, lon: 14.1, topoId: '040' },
  HU: { name: 'Węgry', lat: 47.2, lon: 19.5, topoId: '348' },
  FR: { name: 'Francja', lat: 46.6, lon: 2.5, topoId: '250' },
  NL: { name: 'Holandia', lat: 52.2, lon: 5.3, topoId: '528' },
  BE: { name: 'Belgia', lat: 50.6, lon: 4.7, topoId: '056' },
  LU: { name: 'Luksemburg', lat: 49.8, lon: 6.1, topoId: '442' },
  GB: { name: 'Wielka Brytania', lat: 53.0, lon: -1.5, topoId: '826' },
  IE: { name: 'Irlandia', lat: 53.2, lon: -8.0, topoId: '372' },
  ES: { name: 'Hiszpania', lat: 40.3, lon: -3.7, topoId: '724' },
  PT: { name: 'Portugalia', lat: 39.6, lon: -8.0, topoId: '620' },
  IT: { name: 'Włochy', lat: 42.8, lon: 12.5, topoId: '380' },
  CH: { name: 'Szwajcaria', lat: 46.8, lon: 8.2, topoId: '756' },
  DK: { name: 'Dania', lat: 56.0, lon: 9.5, topoId: '208' },
  SE: { name: 'Szwecja', lat: 59.6, lon: 15.2, topoId: '752' },
  NO: { name: 'Norwegia', lat: 61.0, lon: 9.0, topoId: '578' },
  FI: { name: 'Finlandia', lat: 62.9, lon: 26.0, topoId: '246' },
  EE: { name: 'Estonia', lat: 58.7, lon: 25.5, topoId: '233' },
  LV: { name: 'Łotwa', lat: 56.9, lon: 24.9, topoId: '428' },
  LT: { name: 'Litwa', lat: 55.2, lon: 23.9, topoId: '440' },
  UA: { name: 'Ukraina', lat: 49.0, lon: 31.4, topoId: '804' },
  BY: { name: 'Białoruś', lat: 53.7, lon: 27.9, topoId: '112' },
  RO: { name: 'Rumunia', lat: 45.9, lon: 24.9, topoId: '642' },
  BG: { name: 'Bułgaria', lat: 42.7, lon: 25.3, topoId: '100' },
  GR: { name: 'Grecja', lat: 39.0, lon: 22.0, topoId: '300' },
  HR: { name: 'Chorwacja', lat: 45.5, lon: 16.4, topoId: '191' },
  SI: { name: 'Słowenia', lat: 46.1, lon: 14.8, topoId: '705' },
  RS: { name: 'Serbia', lat: 44.2, lon: 20.8, topoId: '688' },
  BA: { name: 'Bośnia i Hercegowina', lat: 44.2, lon: 17.8, topoId: '070' },
  ME: { name: 'Czarnogóra', lat: 42.7, lon: 19.3, topoId: '499' },
  MK: { name: 'Macedonia Płn.', lat: 41.6, lon: 21.7, topoId: '807' },
  AL: { name: 'Albania', lat: 41.1, lon: 20.1, topoId: '008' },
  MD: { name: 'Mołdawia', lat: 47.2, lon: 28.4, topoId: '498' },
};

// Kontury rysowane dodatkowo (sąsiedzi kadru bez własnych dostaw).
const EXTRA_TOPO_IDS = ['643', '792', '352', '196', '470']; // RU, TR, IS, CY, MT

/* ------------------------------------------------------------------ */
/* Normalizacja nazw (diakrytyki, wielkość liter)                      */
/* ------------------------------------------------------------------ */
export function normalizePlace(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/ł/g, 'l')
    .replace(/ß/g, 'ss')
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    // znaki łączące (diakrytyki) po dekompozycji NFD
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

const CITY_LOOKUP = new Map<string, GeoPlace>();
for (const [label, [lat, lon]] of Object.entries(CITIES_GEO)) {
  CITY_LOOKUP.set(normalizePlace(label), { label, lat, lon });
}
for (const [alias, key] of Object.entries(CITY_ALIASES)) {
  const [lat, lon] = CITIES_GEO[key];
  CITY_LOOKUP.set(normalizePlace(alias), { label: key, lat, lon });
}

// Miasto (i jego egzonimy) -> region administracyjny I rzędu.
const CITY_REGION_LOOKUP = new Map<string, string>();
for (const [city, region] of Object.entries(CITY_REGIONS)) {
  CITY_REGION_LOOKUP.set(normalizePlace(city), region);
}
for (const [alias, key] of Object.entries(CITY_ALIASES)) {
  const region = CITY_REGIONS[key];
  if (region) CITY_REGION_LOOKUP.set(normalizePlace(alias), region);
}

export function findCity(name: string | undefined | null): GeoPlace | null {
  if (!name) return null;
  return CITY_LOOKUP.get(normalizePlace(name)) || null;
}

export function findCountry(code: string | undefined | null): GeoPlace | null {
  if (!code) return null;
  const c = COUNTRIES[code.trim().toUpperCase()];
  return c ? { label: c.name, lat: c.lat, lon: c.lon } : null;
}

export function countryName(code: string | undefined | null): string | null {
  if (!code) return null;
  return COUNTRIES[code.trim().toUpperCase()]?.name || null;
}

/* Odległość ortodromiczna (km) — szacowanie czasu tranzytu paczek na trasie. */
export function distanceKm(a: LatLon, b: LatLon): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/* ------------------------------------------------------------------ */
/* Województwa: nazwa z zamówienia lub przybliżenie z kodu pocztowego  */
/* ------------------------------------------------------------------ */
const VOIVODESHIPS = [
  'Dolnośląskie', 'Kujawsko-Pomorskie', 'Lubelskie', 'Lubuskie', 'Łódzkie',
  'Małopolskie', 'Mazowieckie', 'Opolskie', 'Podkarpackie', 'Podlaskie',
  'Pomorskie', 'Śląskie', 'Świętokrzyskie', 'Warmińsko-Mazurskie',
  'Wielkopolskie', 'Zachodniopomorskie',
];
const VOIVODESHIP_LOOKUP = new Map(VOIVODESHIPS.map((v) => [normalizePlace(v), v]));

// Przybliżone centroidy województw [lat, lon] — punkt na mapie dla zamówień z
// miast spoza słownika CITIES_GEO (małe miejscowości). Dzięki temu KAŻDE polskie
// zamówienie ma pozycję i trafia na mapę/licznik, nawet gdy nie znamy dokładnego
// miasta. Granice okręgów pocztowych ≈ administracyjne, więc to przybliżenie.
const VOIVODESHIP_GEO: Record<string, [number, number]> = {
  'Dolnośląskie': [51.0, 16.4],
  'Kujawsko-Pomorskie': [53.1, 18.4],
  'Lubelskie': [51.2, 22.9],
  'Lubuskie': [52.2, 15.3],
  'Łódzkie': [51.6, 19.4],
  'Małopolskie': [49.9, 20.2],
  'Mazowieckie': [52.3, 21.0],
  'Opolskie': [50.7, 17.9],
  'Podkarpackie': [50.0, 22.2],
  'Podlaskie': [53.3, 22.9],
  'Pomorskie': [54.2, 18.0],
  'Śląskie': [50.3, 19.0],
  'Świętokrzyskie': [50.7, 20.8],
  'Warmińsko-Mazurskie': [53.8, 20.5],
  'Wielkopolskie': [52.3, 17.2],
  'Zachodniopomorskie': [53.6, 15.3],
};

// Pierwsze 2 cyfry kodu pocztowego -> województwo (przybliżenie; granice
// okręgów pocztowych nie pokrywają się 1:1 z administracyjnymi).
const POSTAL_TO_VOIVODESHIP: Record<string, string> = {
  '00': 'Mazowieckie', '01': 'Mazowieckie', '02': 'Mazowieckie', '03': 'Mazowieckie',
  '04': 'Mazowieckie', '05': 'Mazowieckie', '06': 'Mazowieckie', '07': 'Mazowieckie',
  '08': 'Mazowieckie', '09': 'Mazowieckie',
  '10': 'Warmińsko-Mazurskie', '11': 'Warmińsko-Mazurskie', '12': 'Warmińsko-Mazurskie',
  '13': 'Warmińsko-Mazurskie', '14': 'Warmińsko-Mazurskie',
  '15': 'Podlaskie', '16': 'Podlaskie', '17': 'Podlaskie', '18': 'Podlaskie', '19': 'Podlaskie',
  '20': 'Lubelskie', '21': 'Lubelskie', '22': 'Lubelskie', '23': 'Lubelskie', '24': 'Lubelskie',
  '25': 'Świętokrzyskie', '26': 'Świętokrzyskie', '27': 'Świętokrzyskie',
  '28': 'Świętokrzyskie', '29': 'Świętokrzyskie',
  '30': 'Małopolskie', '31': 'Małopolskie', '32': 'Małopolskie', '33': 'Małopolskie', '34': 'Małopolskie',
  '35': 'Podkarpackie', '36': 'Podkarpackie', '37': 'Podkarpackie', '38': 'Podkarpackie', '39': 'Podkarpackie',
  '40': 'Śląskie', '41': 'Śląskie', '42': 'Śląskie', '43': 'Śląskie', '44': 'Śląskie',
  '45': 'Opolskie', '46': 'Opolskie', '47': 'Opolskie', '48': 'Opolskie', '49': 'Opolskie',
  '50': 'Dolnośląskie', '51': 'Dolnośląskie', '52': 'Dolnośląskie', '53': 'Dolnośląskie',
  '54': 'Dolnośląskie', '55': 'Dolnośląskie', '56': 'Dolnośląskie', '57': 'Dolnośląskie',
  '58': 'Dolnośląskie', '59': 'Dolnośląskie',
  '60': 'Wielkopolskie', '61': 'Wielkopolskie', '62': 'Wielkopolskie', '63': 'Wielkopolskie',
  '64': 'Wielkopolskie',
  '65': 'Lubuskie', '66': 'Lubuskie', '67': 'Lubuskie', '68': 'Lubuskie',
  '69': 'Lubuskie',
  '70': 'Zachodniopomorskie', '71': 'Zachodniopomorskie', '72': 'Zachodniopomorskie',
  '73': 'Zachodniopomorskie', '74': 'Zachodniopomorskie', '75': 'Zachodniopomorskie',
  '76': 'Pomorskie', '77': 'Pomorskie', '78': 'Zachodniopomorskie',
  '80': 'Pomorskie', '81': 'Pomorskie', '82': 'Pomorskie', '83': 'Pomorskie', '84': 'Pomorskie',
  '85': 'Kujawsko-Pomorskie', '86': 'Kujawsko-Pomorskie', '87': 'Kujawsko-Pomorskie',
  '88': 'Kujawsko-Pomorskie', '89': 'Kujawsko-Pomorskie',
  '90': 'Łódzkie', '91': 'Łódzkie', '92': 'Łódzkie', '93': 'Łódzkie', '94': 'Łódzkie',
  '95': 'Łódzkie', '96': 'Łódzkie', '97': 'Łódzkie', '98': 'Łódzkie', '99': 'Łódzkie',
};

export function voivodeshipOf(province?: string | null, zip?: string | null): string | null {
  if (province) {
    const hit = VOIVODESHIP_LOOKUP.get(normalizePlace(province.replace(/^wojew[oó]dztwo\s+/i, '')));
    if (hit) return hit;
  }
  const m = (zip || '').trim().match(/^(\d{2})/);
  if (m) return POSTAL_TO_VOIVODESHIP[m[1]] || null;
  return null;
}

/* Przybliżona pozycja na mapie dla polskiego zamówienia spoza słownika miast —
   centroid województwa wyliczonego z pola `province` lub kodu pocztowego. Label =
   nazwa województwa (znaczniki małych miejscowości grupują się regionalnie). */
export function findVoivodeshipPlace(province?: string | null, zip?: string | null): GeoPlace | null {
  const v = voivodeshipOf(province, zip);
  if (!v) return null;
  const c = VOIVODESHIP_GEO[v];
  return c ? { label: v, lat: c[0], lon: c[1] } : null;
}

/* Region administracyjny I rzędu adresu — województwo (PL) lub region/prowincja
   zagraniczna. Kolejność źródeł: pole `province` z zamówienia (dane rzeczywiste),
   a w razie braku — mapa miast. Kraj NIE jest regionem (ma własny ranking). */
export function regionOf(
  countryCode?: string | null,
  province?: string | null,
  zip?: string | null,
  city?: string | null,
): string | null {
  const cc = (countryCode || 'PL').trim().toUpperCase();
  if (cc === 'PL') return voivodeshipOf(province, zip);
  const p = (province || '').trim();
  if (p) return p;
  return CITY_REGION_LOOKUP.get(normalizePlace(city || '')) || null;
}

/* ------------------------------------------------------------------ */
/* Projekcja: equirectangularna z korekcją cos(lat), kadr dynamiczny.  */
/* ------------------------------------------------------------------ */
const EUROPE_BOUNDS = { minLon: -11, maxLon: 41, minLat: 34.5, maxLat: 71.5 };
const POLAND_BBOX = { minLon: 14.1229, maxLon: 24.1458, minLat: 49.002, maxLat: 54.8358 };

type BBox = { minLon: number; maxLon: number; minLat: number; maxLat: number };

export type Projection = {
  project: (lon: number, lat: number) => [number, number];
  bbox: BBox;
};

function fitToCanvas(bbox: BBox): Projection {
  const kx = Math.cos(((bbox.minLat + bbox.maxLat) / 2) * (Math.PI / 180));
  const sx = (MAP_W - 2 * PAD) / ((bbox.maxLon - bbox.minLon) * kx);
  const sy = (MAP_H - 2 * PAD) / (bbox.maxLat - bbox.minLat);
  const s = Math.min(sx, sy);
  const cw = (bbox.maxLon - bbox.minLon) * kx * s;
  const ch = (bbox.maxLat - bbox.minLat) * s;
  const ox = (MAP_W - cw) / 2;
  const oy = (MAP_H - ch) / 2;
  return {
    project: (lon, lat) => [ox + (lon - bbox.minLon) * kx * s, oy + (bbox.maxLat - lat) * s],
    bbox,
  };
}

export function fitProjection(points: LatLon[]): Projection {
  const pts = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
  if (!pts.length) return fitToCanvas(POLAND_BBOX);

  let bbox: BBox = {
    minLon: Math.min(...pts.map((p) => p.lon)),
    maxLon: Math.max(...pts.map((p) => p.lon)),
    minLat: Math.min(...pts.map((p) => p.lat)),
    maxLat: Math.max(...pts.map((p) => p.lat)),
  };

  const inPoland = (p: LatLon) =>
    p.lon >= POLAND_BBOX.minLon && p.lon <= POLAND_BBOX.maxLon &&
    p.lat >= POLAND_BBOX.minLat && p.lat <= POLAND_BBOX.maxLat;

  // Wszystko w Polsce -> dokładnie kadr Polski (stabilny, znajomy widok).
  if (pts.every(inPoland)) return fitToCanvas(POLAND_BBOX);

  // Hub w Polsce -> kraj macierzysty zawsze w całości w kadrze.
  if (inPoland(pts[0])) {
    bbox = {
      minLon: Math.min(bbox.minLon, POLAND_BBOX.minLon),
      maxLon: Math.max(bbox.maxLon, POLAND_BBOX.maxLon),
      minLat: Math.min(bbox.minLat, POLAND_BBOX.minLat),
      maxLat: Math.max(bbox.maxLat, POLAND_BBOX.maxLat),
    };
  }

  // Margines 10% (min ~1.2°) + minimalny sensowny kadr.
  const padLon = Math.max(1.2, (bbox.maxLon - bbox.minLon) * 0.1);
  const padLat = Math.max(0.9, (bbox.maxLat - bbox.minLat) * 0.1);
  bbox = {
    minLon: Math.max(EUROPE_BOUNDS.minLon, bbox.minLon - padLon),
    maxLon: Math.min(EUROPE_BOUNDS.maxLon, bbox.maxLon + padLon),
    minLat: Math.max(EUROPE_BOUNDS.minLat, bbox.minLat - padLat),
    maxLat: Math.min(EUROPE_BOUNDS.maxLat, bbox.maxLat + padLat),
  };
  const minLonSpan = 6;
  const minLatSpan = 4;
  if (bbox.maxLon - bbox.minLon < minLonSpan) {
    const c = (bbox.minLon + bbox.maxLon) / 2;
    bbox.minLon = c - minLonSpan / 2;
    bbox.maxLon = c + minLonSpan / 2;
  }
  if (bbox.maxLat - bbox.minLat < minLatSpan) {
    const c = (bbox.minLat + bbox.maxLat) / 2;
    bbox.minLat = c - minLatSpan / 2;
    bbox.maxLat = c + minLatSpan / 2;
  }
  return fitToCanvas(bbox);
}

/* ------------------------------------------------------------------ */
/* Kontury krajów: dekoder TopoJSON (world-atlas) -> ringi lon/lat     */
/* ------------------------------------------------------------------ */
type Ring = [number, number][];
export type CountryShape = Ring[];

type Topology = {
  transform: { scale: [number, number]; translate: [number, number] };
  arcs: [number, number][][];
  objects: { countries: { geometries: { type: string; id: string | number; arcs: any }[] } };
};

function decodeArc(topo: Topology, i: number): [number, number][] {
  const { scale: sc, translate: tr } = topo.transform;
  let x = 0;
  let y = 0;
  return topo.arcs[i].map(([dx, dy]) => {
    x += dx;
    y += dy;
    return [x * sc[0] + tr[0], y * sc[1] + tr[1]];
  });
}

function ringCoords(topo: Topology, ring: number[]): [number, number][] {
  const pts: [number, number][] = [];
  for (const a of ring) {
    const arc = a >= 0 ? decodeArc(topo, a) : decodeArc(topo, ~a).slice().reverse();
    pts.push(...(pts.length ? arc.slice(1) : arc));
  }
  return pts;
}

function topoCountryToRings(topo: Topology, countryId: string): CountryShape {
  const geom = topo.objects?.countries?.geometries?.find(
    (g) => String(g.id).padStart(3, '0') === countryId.padStart(3, '0'),
  );
  if (!geom) return [];
  const polygons: number[][][] = geom.type === 'Polygon' ? [geom.arcs] : geom.arcs;
  const rings: CountryShape = [];
  for (const poly of polygons) {
    for (const ring of poly) {
      const pts = ringCoords(topo, ring).map(
        ([lon, lat]) => [Math.round(lon * 100) / 100, Math.round(lat * 100) / 100] as [number, number],
      );
      if (pts.length >= 8) rings.push(pts);
    }
  }
  return rings;
}

export function shapeToPath(shape: CountryShape, proj: Projection): string {
  let d = '';
  for (const ring of shape) {
    const pts = ring.map(([lon, lat]) => proj.project(lon, lat));
    // pomijamy ringi w całości poza kadrem (RU/TR ciągną się daleko poza mapę)
    const inView = pts.some(([x, y]) => x > -80 && x < MAP_W + 80 && y > -80 && y < MAP_H + 80);
    if (!inView) continue;
    d += 'M' + pts.map(([x, y]) => x.toFixed(1) + ',' + y.toFixed(1)).join('L') + 'Z';
  }
  return d;
}

/* ------------------------------------------------------------------ */
/* Pobieranie i cache konturów                                          */
/* ------------------------------------------------------------------ */
const GEO_URLS = [
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json',
  'https://unpkg.com/world-atlas@2/countries-50m.json',
];
const CACHE_KEY = 'packflow.map.europe.50m.v1';

// Offline/first-paint fallback: kontur Polski odzyskany z wbudowanej ścieżki
// 1:50m wyrenderowanej starą, stałą projekcją (odwracalne przekształcenie afiniczne).
function legacyPolandRings(): CountryShape {
  const W = 1000;
  const H = 720;
  const P = 16;
  const B = POLAND_BBOX;
  const kx = Math.cos((52 * Math.PI) / 180);
  const sx = (W - 2 * P) / ((B.maxLon - B.minLon) * kx);
  const sy = (H - 2 * P) / (B.maxLat - B.minLat);
  const s = Math.min(sx, sy);
  const cw = (B.maxLon - B.minLon) * kx * s;
  const ch = (B.maxLat - B.minLat) * s;
  const ox = (W - cw) / 2;
  const oy = (H - ch) / 2;
  return LEGACY_POLAND_PATH.split('Z').filter(Boolean).map((seg) =>
    seg
      .replace(/^M/, '')
      .split('L')
      .map((pair) => {
        const [x, y] = pair.split(',').map(Number);
        return [
          Math.round((B.minLon + (x - ox) / (kx * s)) * 100) / 100,
          Math.round((B.maxLat - (y - oy) / s) * 100) / 100,
        ] as [number, number];
      }),
  );
}

export function useEuropeShapes(): CountryShape[] {
  const [shapes, setShapes] = useState<CountryShape[]>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached) as CountryShape[];
    } catch { /* brak storage / zepsuty cache */ }
    return [legacyPolandRings()];
  });

  useEffect(() => {
    try {
      if (localStorage.getItem(CACHE_KEY)) return;
    } catch { return; }
    let cancelled = false;
    (async () => {
      for (const url of GEO_URLS) {
        try {
          const r = await fetch(url);
          if (!r.ok) continue;
          const topo = (await r.json()) as Topology;
          const ids = [...Object.values(COUNTRIES).map((c) => c.topoId), ...EXTRA_TOPO_IDS];
          const all = ids.map((id) => topoCountryToRings(topo, id)).filter((s) => s.length);
          if (all.length && !cancelled) {
            try { localStorage.setItem(CACHE_KEY, JSON.stringify(all)); } catch { /* quota */ }
            setShapes(all);
          }
          return;
        } catch { /* spróbuj kolejne źródło */ }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return shapes;
}

// Natural Earth 1:50m, stara stała projekcja PL — surowiec dla legacyPolandRings().
const LEGACY_POLAND_PATH = 'M824.5,407.2L828.4,415.4L830.0,421.8L828.4,426.7L828.9,431.8L832.3,437.1L843.3,453.5L848.8,469.3L852.2,475.4L860.3,483.4L860.8,486.7L857.7,489.6L855.1,490.0L853.0,490.8L851.7,493.6L853.8,496.7L856.7,501.0L860.1,513.5L859.8,523.7L857.2,526.4L853.5,532.3L851.4,537.9L832.3,541.8L827.9,547.7L817.4,559.2L810.4,565.7L799.9,577.8L783.2,598.3L777.2,606.9L772.7,613.8L759.4,632.7L755.2,640.6L756.0,647.2L760.5,662.6L761.2,669.5L760.5,675.9L759.2,681.6L759.4,684.0L763.3,688.1L769.6,694.7L769.9,696.7L769.1,699.6L766.7,701.9L758.9,699.6L750.3,695.1L747.1,695.7L742.4,694.7L722.8,686.1L709.5,679.5L708.2,675.2L705.8,668.9L700.1,663.8L687.3,659.3L681.8,655.6L660.9,653.7L651.7,653.5L645.5,655.0L641.3,655.0L635.5,664.2L631.6,666.8L625.8,667.1L620.9,665.4L615.7,660.7L607.5,658.0L601.5,659.3L597.4,658.3L593.4,658.0L592.1,658.9L589.3,658.9L584.8,661.1L580.1,664.4L574.6,666.8L570.7,672.4L567.0,682.8L556.8,678.1L553.4,680.2L548.5,681.6L545.1,680.2L545.9,676.5L547.4,672.4L547.4,666.6L546.4,660.3L543.2,658.3L538.5,657.4L535.9,656.2L535.7,654.2L533.3,651.5L529.1,644.7L525.0,636.3L522.3,633.7L518.4,637.8L512.1,642.3L508.5,643.9L501.2,657.0L487.8,657.4L487.0,651.3L485.7,645.6L478.2,643.9L477.9,640.4L476.3,631.8L460.9,614.8L458.8,607.7L459.6,605.0L458.6,600.5L455.2,597.9L442.9,594.6L439.7,596.4L436.9,594.6L432.4,590.5L424.8,587.2L424.1,585.4L421.2,582.5L419.6,582.1L418.8,583.9L416.5,586.4L408.6,589.7L405.5,588.2L402.6,585.6L399.2,579.6L394.5,574.5L390.6,572.7L388.2,569.8L387.7,567.8L396.6,563.5L398.4,559.2L397.4,551.2L396.1,550.2L392.7,552.8L385.4,555.1L378.6,556.3L375.2,556.3L356.1,541.8L343.5,537.3L336.2,536.0L335.4,537.5L338.8,545.6L344.6,555.7L344.3,558.3L337.5,562.4L333.6,564.3L328.9,567.8L325.0,572.7L321.9,574.7L318.7,574.3L315.8,571.9L307.7,556.9L297.8,545.6L296.8,543.0L293.6,542.4L289.2,539.9L287.6,536.2L290.0,532.7L292.8,529.3L298.3,527.2L299.9,525.4L300.9,522.5L302.8,518.6L302.2,517.4L298.6,513.1L292.8,509.0L277.2,512.1L273.0,514.1L270.6,511.5L268.8,507.2L264.6,506.5L259.4,502.7L252.8,499.0L246.6,497.9L233.5,492.6L228.5,492.4L225.7,490.6L222.5,486.5L219.9,482.2L218.6,473.2L208.9,469.3L199.3,466.6L198.7,468.1L199.0,476.9L198.5,481.8L192.2,484.6L185.9,485.0L186.5,483.4L193.8,467.4L197.2,457.2L201.1,438.6L196.4,423.8L195.1,417.1L193.0,413.8L179.9,406.6L178.9,404.2L181.0,394.4L179.9,390.3L176.8,386.0L172.6,377.6L170.8,370.2L176.3,361.6L177.6,355.5L179.9,346.9L181.5,342.1L181.7,340.7L178.4,337.4L177.6,332.7L178.4,326.0L176.5,320.9L171.8,317.8L168.9,313.5L167.4,308.0L168.7,299.6L172.1,288.1L164.5,274.4L145.7,258.2L136.5,246.9L137.3,240.4L141.2,234.7L148.6,229.3L154.0,220.1L156.9,209.1L157.2,207.0L157.4,199.0L148.8,166.9L147.5,158.9L146.5,149.1L145.9,146.4L162.7,153.2L169.5,157.1L168.7,152.8L167.4,149.1L168.2,143.6L167.6,135.4L152.7,131.3L142.8,129.8L141.5,124.1L142.5,120.4L145.4,122.7L155.1,123.5L179.1,112.4L220.7,98.1L265.1,84.6L275.6,83.2L286.0,80.3L289.7,75.4L293.6,71.9L299.6,63.1L313.0,49.2L336.8,44.3L345.6,37.7L363.9,28.5L406.3,18.3L423.8,16.0L441.0,15.8L456.5,23.8L472.7,33.8L475.8,40.0L466.9,36.1L454.1,27.1L449.4,26.6L460.4,54.1L466.1,63.7L478.4,71.1L488.6,73.3L519.7,69.0L531.0,63.3L534.1,60.4L537.0,61.9L557.4,63.3L578.0,64.9L611.2,66.6L645.7,68.4L681.5,70.3L720.2,72.1L761.2,73.3L763.6,72.5L767.8,67.8L773.0,68.4L779.0,71.3L781.9,73.5L782.9,76.0L783.7,78.7L787.1,79.3L793.1,81.3L801.2,86.2L807.5,90.9L813.5,97.7L815.6,105.3L815.6,113.9L815.4,119.4L815.9,121.6L824.2,161.8L838.1,200.5L843.1,219.3L845.2,229.3L846.7,243.7L847.2,253.9L847.2,259.6L846.2,267.4L842.0,272.1L815.6,285.4L810.4,289.5L802.8,300.0L795.5,310.6L793.9,314.3L793.4,316.6L795.0,320.2L804.4,325.8L814.0,330.5L817.2,334.0L824.0,338.3L826.6,342.1L828.2,345.6L827.9,353.6L824.8,364.7L826.1,372.9L822.9,378.4L820.3,384.5L819.8,395.4L824.5,407.2Z';
